import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { HttpService } from '@app/core/services/http.service';
import { environment } from '@environments/environment';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { TimesheetState } from '@timesheet/state/timesheet.state';
import { Timesheet, TimesheetEventItem, TimesheetPagination } from '@timesheet/state/timesheet.types';
import { map, Observable, of, switchMap, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  public static readonly API: string = `${environment.PROJECT_API}/timesheet-group/timesheets`;
  public static readonly AUTOFILL_API: string = `${environment.PROJECT_API}/timesheet-group/autofill`;

  protected readonly httpService: HttpService = inject(HttpService);
  protected readonly state: TimesheetState = inject(TimesheetState);

  getTimesheets(queryParams?: Params): Observable<TimesheetPagination> {
    const defaultParams = {
      page: '1',
      limit: '20',
      sort: ''
    };

    return this.httpService.get<TimesheetPagination>(`${TimesheetService.API}`, {
      params: new HttpParams({
        fromObject: queryParams && Object.keys(queryParams)?.length ? queryParams : defaultParams
      })
    });
  }

  getExcelTimesheets(queryParams: Params): Observable<Timesheet[]> {
    return this.httpService
      .get<TimesheetPagination>(`${TimesheetService.API}/excels`, {
        params: {
          ...queryParams
        }
      })
      .pipe(map((requests) => requests.data));
  }

  getTimesheetById(id: string): Observable<Timesheet> {
    return this.httpService.get<{ data: Timesheet }>(`${TimesheetService.API}/${id}`, {}).pipe(
      switchMap((request) => {
        if (!request.data) {
          return throwError(() => new Error(`Could not find timesheet with id of ${id}!`));
        }

        return of(request.data);
      })
    );
  }

  createTimesheet(request: URLSearchParams): Observable<Timesheet> {
    return this.httpService
      .post<{ data: Timesheet }>(`${TimesheetService.API}`, request.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;'
        }
      })
      .pipe(map((newTimesheet) => newTimesheet.data));
  }

  updateTimesheet(id: string, request: URLSearchParams): Observable<Timesheet> {
    return this.httpService
      .put<{ data: Timesheet }>(`${TimesheetService.API}/${id}`, request.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;'
        }
      })
      .pipe(map((updatedTimesheet) => updatedTimesheet.data));
  }

  deleteTimesheet(id: string): Observable<boolean> {
    return this.httpService.delete(`${TimesheetService.API}/${id}`, {}).pipe(map((isDeleted: boolean) => isDeleted));
  }

  getTimesheetEventItems(queryParams?: Params) {
    const defaultParams = {
      page: '1',
      limit: LIMIT_PER_PAGE_DEFAULT
    };

    return this.httpService
      .get<any>(`${TimesheetService.AUTOFILL_API}/timesheets/event_items`, {
        params: new HttpParams({ fromObject: queryParams && Object.keys(queryParams)?.length ? queryParams : defaultParams })
      })
      .pipe(
        map((response) => ({
          pagination: {
            current_page: response.current_page,
            last_page: response.last_page,
            per_page: response.per_page,
            total: response.total
          },
          data: response.data
        })),
        switchMap((response) => {
          if (response.data === null) {
            return throwError(() => new Error('Requested page is not available!'));
          }

          return of(response);
        })
      );
  }

  searchEventItemsByName(event_name: string, limit?: number): Observable<TimesheetEventItem[]> {
    const queryParams = {
      limit: limit ?? LIMIT_PER_PAGE_DEFAULT,
      'filter[name]': event_name
    };

    return this.httpService
      .get<{ data: TimesheetEventItem[] }>(`${TimesheetService.AUTOFILL_API}/timesheets/event_items`, {
        params: queryParams
      })
      .pipe(
        map((items) => {
          return items.data;
        }),
        tap((items) => {
          this.state.set({ searchedEventItems: items });
        })
      );
  }

  updateTimesheetEvents(id: string, request: URLSearchParams): Observable<Timesheet> {
    return this.httpService
      .post<{ data: Timesheet }>(`${TimesheetService.API}/${id}/timesheet_events`, request.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;'
        }
      })
      .pipe(map((updatedTimesheet) => updatedTimesheet.data));
  }
}
